import { useEffect, useMemo, useRef, useState } from 'react';
import {
  scenarioFigures,
  type DealDetail,
  type DealScenarioDetail,
  type ScenarioCost,
  type ScenarioKind,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  Empty,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  cn,
  type MenuItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { formatCalendarDate } from '../history/history-labels';
import { ScreenState } from '../screen-state';
import { dealMoney, dealMoneyShort } from './deal-kit';
import { PlanConfirm } from './plan-kit';
import { ScenarioCostDialog } from './scenario-dialogs';
import { ScenarioGrid } from './scenario-grid';
import {
  monthsPerPeriod,
  pctText,
  scenarioInput,
  scenarioStatusLabel,
  scenarioStatusVariant,
  spanText,
  useScenarioDetail,
} from './scenario-kit';
import { ScenarioSchedule } from './scenario-schedule';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · one scenario — `07b`, `07f`, `07g`, `07h`.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The header (back, the name, the status, *Nyertessé jelölés*, the kebab), seven tiles, the
 * contract-type and view menus, then the grid — or, on a recurring scenario, the two-step switch
 * between the period template and the schedule — and the external costs.
 *
 * ⚠️ **The tiles are `scenarioFigures` of what is saved**, with the *opciókkal* switch applied
 * (DS-5 — a view switch, never stored). The grid's footer also counts its unsaved drafts.
 *
 * ⚠️ **Read-only, and the banner says why**: no `deal.manage`, a won or lost deal (DS-6), or a
 * scenario dropped when another won (DS-1 keeps those for reference).
 */

type Step = 1 | 2;

export function ScenarioEditor({
  deal,
  scenarioId,
  writable,
  revision,
  rateCurrency,
  profiles,
  onBack,
  onOpen,
  onMarkWinner,
  onTab,
  onDealChanged,
  toast,
}: {
  readonly deal: DealDetail;
  readonly scenarioId: string;
  /** `deal.manage`, and the deal still open. */
  readonly writable: boolean;
  /** Bumped by the tab after a write it made (a winner) — reload. */
  readonly revision: number;
  readonly rateCurrency: string;
  readonly profiles: Parameters<typeof ScenarioGrid>[0]['profiles'];
  readonly onBack: () => void;
  readonly onOpen: (id: string) => void;
  readonly onMarkWinner: (scenario: { id: string; name: string }) => void;
  readonly onTab: (tab: 'plan') => void;
  /** The deal itself changed (a winner unmarked answers with the deal) — reload the record. */
  readonly onDealChanged: () => void;
  readonly toast: (title: string, variant?: 'success' | 'error') => void;
}) {
  const { detail, loading, failure, reload, write, send, call } = useScenarioDetail(
    deal.id,
    scenarioId,
  );

  useEffect(() => {
    if (revision > 0) void reload();
  }, [revision, reload]);

  return (
    <div className="flex flex-col pb-10">
      {failure !== null ? (
        <>
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <BackButton onBack={onBack} />
          </div>
          <ScreenState failure={failure} onRetry={() => void reload()}>
            {null}
          </ScreenState>
        </>
      ) : detail === null ? (
        <div className="flex flex-col gap-3 px-4 py-4" aria-busy={loading}>
          <Skeleton width={30} />
          <Skeleton width={90} />
          <Skeleton width={70} />
        </div>
      ) : (
        <EditorBody
          deal={deal}
          detail={detail}
          writable={writable}
          rateCurrency={rateCurrency}
          profiles={profiles}
          write={write}
          send={send}
          call={call}
          onBack={onBack}
          onOpen={onOpen}
          onMarkWinner={onMarkWinner}
          onTab={onTab}
          onUnmarked={() => {
            void reload();
            onDealChanged();
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

function BackButton({ onBack }: { readonly onBack: () => void }) {
  const t = useT();
  return (
    <Button variant="ghost" size="sm" className="-ml-1 gap-1.5 px-1.5" onClick={onBack}>
      <Icon name="chevron-left" size={14} />
      {t('dealScenario.back')}
    </Button>
  );
}

function EditorBody({
  deal,
  detail,
  writable,
  rateCurrency,
  profiles,
  write,
  send,
  call,
  onBack,
  onOpen,
  onMarkWinner,
  onTab,
  onUnmarked,
  toast,
}: {
  readonly deal: DealDetail;
  readonly detail: DealScenarioDetail;
  readonly writable: boolean;
  readonly rateCurrency: string;
  readonly profiles: Parameters<typeof ScenarioGrid>[0]['profiles'];
  readonly write: ReturnType<typeof useScenarioDetail>['write'];
  readonly send: ReturnType<typeof useScenarioDetail>['send'];
  readonly call: ReturnType<typeof useScenarioDetail>['call'];
  readonly onBack: () => void;
  readonly onOpen: (id: string) => void;
  readonly onMarkWinner: (scenario: { id: string; name: string }) => void;
  readonly onTab: (tab: 'plan') => void;
  readonly onUnmarked: () => void;
  readonly toast: (title: string, variant?: 'success' | 'error') => void;
}) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const nameRef = useRef<HTMLInputElement>(null);
  const [withOptions, setWithOptions] = useState(true);
  const [step, setStep] = useState<Step>(1);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [costDialog, setCostDialog] = useState<{ cost: ScenarioCost | null } | null>(null);
  const [pending, setPending] = useState<
    { kind: 'scenario' } | { kind: 'cost'; cost: ScenarioCost } | null
  >(null);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const currency = detail.currencyCode;
  const recurring = detail.kind === 'recurring';
  const dealClosed = deal.statusCategory !== 'open';
  const readOnly = !writable || !detail.canManage || detail.status === 'dropped';
  const roNote = dealClosed
    ? t('dealScenario.readOnly.closed')
    : detail.status === 'dropped'
      ? t('dealScenario.readOnly.dropped')
      : readOnly
        ? t('dealScenario.readOnly.denied')
        : null;

  const input = useMemo(() => scenarioInput(detail, withOptions), [detail, withOptions]);
  const figures = useMemo(() => scenarioFigures(input), [input]);
  const months = recurring
    ? figures.span * monthsPerPeriod(detail.periodUnit, detail.periodStep)
    : figures.span;
  const optionSum = detail.lines
    .filter((line) => line.type === 'option')
    .reduce((sum, line) => sum + Number(line.amount), 0);

  /* ── writes ── */
  const patch = async (body: Record<string, unknown>): Promise<boolean> => {
    setError(null);
    const refusal = await write('', 'PATCH', { version: detail.version, ...body });
    if (refusal !== null) {
      setError(refusal);
      return false;
    }
    return true;
  };

  const commitName = async (): Promise<void> => {
    if (nameDraft === null) return;
    const name = nameDraft.trim();
    if (name === '') {
      setError(t('dealScenario.error.name'));
      return;
    }
    if (name === detail.name || (await patch({ name }))) setNameDraft(null);
  };

  const setKind = async (kind: ScenarioKind): Promise<void> => {
    if (kind === detail.kind) return;
    if (await patch({ kind })) setStep(1);
  };

  const addLine = async (): Promise<void> => {
    setError(null);
    const refusal = await write('/lines', 'POST', {
      name: t('dealScenario.line.newName'),
      type: 'fixed',
      amount: 0,
      startsOn: null,
      endsOn: null,
    });
    if (refusal !== null) setError(refusal);
  };

  const duplicate = async (): Promise<void> => {
    const answer = await call('', 'POST', {
      name: t('dealScenario.copyName', { name: detail.name }).slice(0, 120),
      kind: detail.kind,
      startsOn: detail.startsOn,
      copyFromId: detail.id,
    });
    if (typeof answer === 'string') {
      toast(answer, 'error');
      return;
    }
    const body = (await answer.json().catch(() => null)) as { id?: unknown } | null;
    toast(t('dealScenario.toast.duplicated'));
    if (typeof body?.id === 'string') onOpen(body.id);
  };

  const unmark = async (): Promise<void> => {
    setError(null);
    /* The answer is the deal, not the scenario — reload both. */
    const answer = await call(`/${detail.id}/winner`, 'DELETE');
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    onUnmarked();
    toast(t('dealScenario.toast.unmarked'));
  };

  const confirmPending = async (): Promise<void> => {
    if (pending === null) return;
    if (pending.kind === 'cost') {
      const refusal = await write(`/costs/${pending.cost.id}`, 'DELETE');
      if (refusal !== null) {
        setPendingError(refusal);
        return;
      }
      setPending(null);
      return;
    }
    const answer = await call(`/${detail.id}`, 'DELETE');
    if (typeof answer === 'string') {
      setPendingError(answer);
      return;
    }
    setPending(null);
    toast(t('dealScenario.toast.deleted', { name: detail.name }));
    onBack();
  };

  /* ── menus ── */
  const kebab: MenuItem[] = [
    ...(writable
      ? [
          {
            label: t('dealScenario.action.duplicate'),
            icon: 'copy' as const,
            onSelect: () => void duplicate(),
          },
        ]
      : []),
    ...(readOnly
      ? []
      : [
          {
            label: t('dealScenario.action.rename'),
            icon: 'pencil' as const,
            onSelect: () => {
              nameRef.current?.focus();
              nameRef.current?.select();
            },
          },
        ]),
    ...(writable && detail.isWinner
      ? [
          {
            label: t('dealScenario.action.unmark'),
            icon: 'x' as const,
            onSelect: () => void unmark(),
          },
        ]
      : []),
    ...(!readOnly && (detail.status === 'draft' || detail.status === 'sent')
      ? [
          { type: 'label' as const, label: t('dealScenario.column.status') },
          {
            label: t('dealScenario.status.draft'),
            checked: detail.status === 'draft',
            onSelect: () => void patch({ status: 'draft' }),
          },
          {
            label: t('dealScenario.status.sent'),
            checked: detail.status === 'sent',
            onSelect: () => void patch({ status: 'sent' }),
          },
        ]
      : []),
    ...(writable
      ? [
          { type: 'separator' as const },
          {
            label: t('dealScenario.action.delete'),
            icon: 'trash' as const,
            destructive: true,
            onSelect: () => {
              setPendingError(null);
              setPending({ kind: 'scenario' });
            },
          },
        ]
      : []),
  ];

  const kindItems: MenuItem[] = (['one_off', 'recurring'] as const).map((kind) => ({
    label: t(
      kind === 'recurring'
        ? 'dealScenario.kind.recurringContract'
        : 'dealScenario.kind.oneOffContract',
    ),
    description:
      kind === 'recurring'
        ? t('dealScenario.kind.recurringHint')
        : t('dealScenario.kind.oneOffHint'),
    checked: detail.kind === kind,
    disabled: readOnly,
    onSelect: () => void setKind(kind),
  }));

  const viewItems: MenuItem[] = [
    { type: 'label', label: t('dealScenario.view.options') },
    {
      label: t('dealScenario.view.withOptions'),
      hint: dealMoney(optionSum, currency),
      checked: withOptions,
      onSelect: () => {
        setWithOptions(true);
      },
    },
    {
      label: t('dealScenario.view.withoutOptions'),
      checked: !withOptions,
      onSelect: () => {
        setWithOptions(false);
      },
    },
  ];

  const tiles = [
    {
      key: 'revenue',
      label: t('dealScenario.tile.revenue'),
      value: dealMoneyShort(figures.revenue, currency),
      title: dealMoney(figures.revenue, currency),
      sub: withOptions ? t('dealScenario.tile.withOptions') : t('dealScenario.tile.withoutOptions'),
      negative: false,
    },
    {
      key: 'labour',
      label: t('dealScenario.tile.labour'),
      value: dealMoneyShort(figures.labour, currency),
      title: dealMoney(figures.labour, currency),
      sub: t('dealScenario.tile.allocated', { hours: fmt(figures.hours) }),
      negative: false,
    },
    {
      key: 'external',
      label: t('dealScenario.tile.external'),
      value: dealMoneyShort(figures.external, currency),
      title: dealMoney(figures.external, currency),
      sub: t('dealScenario.tile.costRows', { count: detail.costs.length }),
      negative: false,
    },
    {
      key: 'margin',
      label: t('dealScenario.tile.margin'),
      value: dealMoneyShort(figures.margin, currency),
      title: dealMoney(figures.margin, currency),
      sub: t('dealScenario.tile.marginSub', { pct: pctText(figures.marginPct, locale) }),
      negative: figures.margin < 0,
    },
    {
      key: 'hours',
      label: t('dealScenario.tile.hours'),
      value: fmt(figures.hours),
      title: fmt(figures.hours),
      sub: t('dealScenario.tile.fte', {
        fte: new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(figures.hours / Math.max(1, months) / 168),
      }),
      negative: false,
    },
    {
      key: 'span',
      label: t('dealScenario.tile.span'),
      value: recurring
        ? t('dealScenario.tile.periods', { count: figures.span })
        : t('dealScenario.tile.months', { count: figures.span }),
      title: spanText(figures.from, figures.to, locale),
      sub: spanText(figures.from, figures.to, locale),
      negative: false,
    },
    {
      key: 'perHour',
      label: t('dealScenario.tile.perHour'),
      value:
        figures.hours > 0 ? dealMoney(Math.round(figures.revenue / figures.hours), currency) : '—',
      title: '',
      sub: figures.hours > 0 ? t('dealScenario.tile.perHourSub') : t('dealScenario.tile.noHours'),
      negative: false,
    },
  ];

  return (
    <>
      {/* ── the header ── */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 border-b px-3 py-2">
        <BackButton onBack={onBack} />
        <input
          ref={nameRef}
          aria-label={t('dealScenario.field.name')}
          readOnly={readOnly}
          value={nameDraft ?? detail.name}
          onChange={(event) => {
            setNameDraft(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void commitName();
            } else if (event.key === 'Escape') {
              setNameDraft(null);
            }
          }}
          className={cn(
            'hover:ring-input focus:ring-ring/50 min-w-0 flex-1 rounded-md bg-transparent px-1.5 py-1 text-[15px] font-semibold outline-none hover:ring-1 focus:ring-2 read-only:hover:ring-0',
            nameDraft !== null && 'bg-accent/60',
          )}
        />
        <Badge variant={scenarioStatusVariant(detail.status)}>
          {scenarioStatusLabel(t, detail.status)}
        </Badge>
        {detail.isWinner ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onTab('plan');
            }}
          >
            <Icon name="arrow-right" size={14} />
            {t('dealScenario.action.openPlan')}
          </Button>
        ) : !readOnly ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onMarkWinner({ id: detail.id, name: detail.name });
            }}
          >
            <Icon name="circle-check" size={14} />
            {t('dealScenario.action.markWinner')}
          </Button>
        ) : null}
        {kebab.length === 0 ? null : (
          <DropdownMenu label={t('dealScenario.menu')} align="end" width={248} items={kebab}>
            <Button variant="ghost" size="icon-sm" aria-label={t('dealScenario.menu')}>
              <Icon name="ellipsis" size={16} />
            </Button>
          </DropdownMenu>
        )}
      </div>

      {roNote === null ? null : (
        <div className="flex items-center gap-2.5 border-b bg-[color-mix(in_oklch,var(--muted)_50%,var(--background))] px-4 py-2">
          <Icon name="shield-check" size={14} className="text-muted-foreground shrink-0" />
          <p className="text-[12px]">{roNote}</p>
        </div>
      )}

      {/* ── the tiles ── */}
      <div className="grid grid-cols-2 border-b md:grid-cols-4 xl:grid-cols-7">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="border-r border-b px-3 py-2.5 last:border-r-0 xl:border-b-0"
          >
            <div className="text-muted-foreground truncate text-[11.5px]">{tile.label}</div>
            <Tooltip asChild content={tile.title === '' ? undefined : tile.title}>
              <div
                className={cn(
                  'mt-0.5 truncate text-[15px] font-medium whitespace-nowrap tabular-nums',
                  tile.negative && 'text-destructive',
                )}
              >
                {tile.value}
              </div>
            </Tooltip>
            <div className="text-muted-foreground truncate text-[11px] tabular-nums">
              {tile.sub}
            </div>
          </div>
        ))}
      </div>

      {/* ── the two steps of a recurring scenario ── */}
      {recurring ? (
        <div
          className="flex flex-wrap items-center gap-1.5 border-b bg-[color-mix(in_oklch,var(--muted)_30%,var(--background))] px-3 py-2"
          role="tablist"
          aria-label={t('dealScenario.steps.label')}
        >
          {([1, 2] as const).map((one) => {
            const active = step === one;
            return (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setStep(one);
                }}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[12px] transition-colors',
                  active
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'text-muted-foreground hover:bg-accent/60',
                )}
              >
                <span
                  className={cn(
                    'grid size-4 place-items-center rounded-full text-[10px] font-semibold',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted',
                  )}
                >
                  {one}
                </span>
                <span>
                  {one === 1 ? t('dealScenario.steps.template') : t('dealScenario.steps.schedule')}
                </span>
                <span className="text-[11px] opacity-70">
                  {one === 1
                    ? t('dealScenario.steps.templateHint')
                    : t('dealScenario.steps.scheduleHint', { count: detail.periodCount })}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* ── the contract type, the view, the one action ── */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 border-b px-3 py-2">
        <DropdownMenu
          label={t('dealScenario.kind.menu')}
          align="start"
          width={300}
          items={kindItems}
        >
          <Button variant="outline" size="sm">
            <Icon name={recurring ? 'refresh-cw' : 'file-text'} size={14} />
            {recurring
              ? t('dealScenario.kind.recurringContract')
              : t('dealScenario.kind.oneOffContract')}
            <Icon name="chevron-down" size={12} className="opacity-60" />
          </Button>
        </DropdownMenu>
        <DropdownMenu
          label={t('dealScenario.view.menu')}
          align="start"
          width={260}
          items={viewItems}
        >
          <Button variant="outline" size="sm">
            <Icon name="sliders-horizontal" size={14} />
            {withOptions ? t('dealScenario.view.labelWith') : t('dealScenario.view.labelWithout')}
          </Button>
        </DropdownMenu>
        {error === null ? null : (
          <span className="text-destructive text-[12px]" role="alert">
            {error}
          </span>
        )}
        <div className="flex-1" />
        {!recurring || step === 1 ? (
          <>
            <span className="text-muted-foreground hidden text-[12px] lg:inline">
              {recurring ? t('dealScenario.grid.hintRecurring') : t('dealScenario.grid.hint')}
            </span>
            {readOnly ? null : (
              <Button size="sm" onClick={() => void addLine()}>
                <Icon name="plus" size={14} />
                {t('dealScenario.action.newLine')}
              </Button>
            )}
          </>
        ) : null}
      </div>

      {recurring && step === 2 ? (
        <ScenarioSchedule
          detail={detail}
          withOptions={withOptions}
          readOnly={readOnly}
          write={write}
        />
      ) : (
        <>
          {detail.lines.length === 0 ? (
            <Empty
              icon="square-dashed"
              title={t('dealScenario.lines.emptyTitle')}
              body={t('dealScenario.lines.emptyBody')}
              {...(readOnly
                ? {}
                : {
                    actionLabel: t('dealScenario.action.newLine'),
                    onAction: () => void addLine(),
                  })}
            />
          ) : (
            <ScenarioGrid
              detail={detail}
              withOptions={withOptions}
              readOnly={readOnly}
              send={send}
              profiles={profiles}
              rateCurrency={rateCurrency}
            />
          )}

          {/* ── external costs ── */}
          <section className="border-t">
            <div className="flex items-center gap-2 px-4 pt-4 pb-2">
              <h2 className="text-[13px] font-medium">{t('dealScenario.cost.title')}</h2>
              {detail.costs.length > 0 ? (
                <span className="text-muted-foreground text-[12px] tabular-nums">
                  {t('dealScenario.cost.summary', {
                    amount: dealMoney(figures.external, currency),
                  })}
                </span>
              ) : null}
              <div className="flex-1" />
              {readOnly ? null : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCostDialog({ cost: null });
                  }}
                >
                  <Icon name="plus" size={14} />
                  {t('dealScenario.cost.add')}
                </Button>
              )}
            </div>
            {detail.costs.length === 0 ? (
              <p className="text-muted-foreground px-4 py-2 text-[12.5px]">
                {t('dealScenario.cost.empty')}
              </p>
            ) : (
              <Table label={t('dealScenario.cost.title')}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-4">{t('dealScenario.cost.name')}</TableHead>
                    <TableHead className="text-right">{t('dealScenario.cost.amount')}</TableHead>
                    <TableHead>{t('dealScenario.cost.date')}</TableHead>
                    <TableHead className="w-10" aria-label={t('list.rowMenu')} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.costs.map((cost) => (
                    <TableRow key={cost.id}>
                      <TableCell className="pl-4">
                        <span className="flex items-center gap-2">
                          <Icon name="credit-card" size={14} className="text-muted-foreground" />
                          <span className="font-medium">{cost.name}</span>
                        </span>
                      </TableCell>
                      <TableCell numeric>{dealMoney(cost.amount, currency)}</TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {cost.incurredOn === null
                          ? '—'
                          : formatCalendarDate(cost.incurredOn, locale)}
                      </TableCell>
                      <TableCell className="pr-3">
                        {readOnly ? null : (
                          <DropdownMenu
                            label={t('list.rowMenu')}
                            align="end"
                            items={[
                              {
                                label: t('common.edit'),
                                icon: 'pencil',
                                onSelect: () => {
                                  setCostDialog({ cost });
                                },
                              },
                              {
                                label: t('dealScenario.cost.delete'),
                                icon: 'trash',
                                destructive: true,
                                onSelect: () => {
                                  setPendingError(null);
                                  setPending({ kind: 'cost', cost });
                                },
                              },
                            ]}
                          >
                            <Button variant="ghost" size="icon-sm" aria-label={t('list.rowMenu')}>
                              <Icon name="ellipsis" size={16} />
                            </Button>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </section>
        </>
      )}

      <ScenarioCostDialog
        open={costDialog !== null}
        cost={costDialog?.cost ?? null}
        write={write}
        onClose={() => {
          setCostDialog(null);
        }}
      />
      <PlanConfirm
        open={pending !== null}
        title={
          pending?.kind === 'cost'
            ? t('dealScenario.confirm.costTitle')
            : t('dealScenario.confirm.deleteTitle')
        }
        body={
          pending?.kind === 'cost'
            ? t('dealScenario.confirm.costBody', { name: pending.cost.name })
            : t('dealScenario.confirm.deleteBody', { name: detail.name })
        }
        confirmLabel={t('common.delete')}
        error={pendingError}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => void confirmPending()}
      />
    </>
  );
}
